/**
 * Teacher Equity — ministry mock data (multi-teacher cohorts, peer ranges, overview metrics).
 * Replace with PSB aggregates; keep export shapes stable for API mapping.
 */

import { getDefaultThresholdFromConfig } from '@/features/settings/utils/resolveDashboardSetting';

export type TeacherEquitySpreadThresholds = {
  highlightAmountUsd: number;
  highlightPercentOfMedian: number;
  severityVeryHighRatio: number;
  severityHighRatio: number;
  severityVeryHighAmountUsd: number;
  severityHighAmountUsd: number;
};

export function defaultTeacherEquitySpreadThresholds(): TeacherEquitySpreadThresholds {
  return {
    highlightAmountUsd: 10000,
    highlightPercentOfMedian: 20,
    severityVeryHighRatio: getDefaultThresholdFromConfig('Teacher Equity Spread Severity Very High Ratio'),
    severityHighRatio: getDefaultThresholdFromConfig('Teacher Equity Spread Severity High Ratio'),
    severityVeryHighAmountUsd: getDefaultThresholdFromConfig(
      'Teacher Equity Spread Severity Very High Amount USD'
    ),
    severityHighAmountUsd: getDefaultThresholdFromConfig('Teacher Equity Spread Severity High Amount USD'),
  };
}

export type EmploymentType = 'permanent' | 'contractual';

export type ComparisonMode =
  | 'intra_school'
  | 'by_district'
  | 'by_enrollment_band'
  | 'by_tuition_band'
  | 'by_parent_tuition';

export interface CompensationBreakdown {
  baseSalary: number;
  extraWages: number;
  weeklyHours: number | null;
  transport: number;
  nssf: number;
  familyAllowance: number | null;
  endOfServiceMonthly: number | null;
  specialTaskWages: number | null;
}

export interface TeacherEquityPerson {
  id: string;
  displayName: string;
  employmentType: EmploymentType;
  roleId: string;
  qualificationId: string;
  totalSalaryAnnual: number;
  breakdown: CompensationBreakdown;
}

export interface SchoolEquityRecord {
  schoolId: string;
  schoolName: string;
  districtId: string;
  districtLabel: string;
  enrollmentBandId: string;
  enrollmentBandLabel: string;
  tuitionBandId: string;
  tuitionBandLabel: string;
  parentTuitionBandId: string;
  parentTuitionBandLabel: string;
  permanentTeachers: TeacherEquityPerson[];
  contractualTeachers: TeacherEquityPerson[];
}

export const ACADEMIC_YEARS = [
  { id: '2025-2026', label: '2025–2026' },
  { id: '2024-2025', label: '2024–2025' },
  { id: '2023-2024', label: '2023–2024' },
];

/**
 * Maps the reporting year from budget APIs (e.g. "2025") to mock cohort keys (e.g. "2025-2026").
 * Pass-through for values that are already range-shaped.
 */
export function equityMockYearIdFromReportingSelection(selection: string): string {
  const t = selection.trim();
  if (!t) return ACADEMIC_YEARS[0]!.id;
  if (/^\d{4}$/.test(t)) {
    const y = parseInt(t, 10);
    if (!Number.isFinite(y)) return ACADEMIC_YEARS[0]!.id;
    return `${y}-${y + 1}`;
  }
  return t;
}

export const ROLES = [
  { id: 'math', label: 'Math Teacher' },
  { id: 'science', label: 'Science Teacher' },
  { id: 'arabic', label: 'Arabic Language Teacher' },
  { id: 'homeroom', label: 'Homeroom Teacher' },
];

export const QUALIFICATIONS = [
  { id: 'bach', label: "Bachelor's" },
  { id: 'mast', label: "Master's" },
  { id: 'dip', label: 'Teaching Diploma' },
  { id: 'phd', label: 'PhD' },
];

/** Global filter: aggregate across every concrete role × qualification pair. */
export const FILTER_ALL = 'all' as const;
export type RoleFilterId = typeof FILTER_ALL | (typeof ROLES)[number]['id'];
export type QualificationFilterId = typeof FILTER_ALL | (typeof QUALIFICATIONS)[number]['id'];

export const ROLES_WITH_ALL = [{ id: FILTER_ALL, label: 'All roles' }, ...ROLES] as const;
export const QUALIFICATIONS_WITH_ALL = [{ id: FILTER_ALL, label: 'All qualifications' }, ...QUALIFICATIONS] as const;

export const YEAR_FACTOR: Record<string, number> = {
  '2025-2026': 1.032,
  '2024-2025': 1,
  '2023-2024': 0.968,
};

export const ROLE_FACTOR: Record<string, number> = {
  math: 1,
  science: 1.05,
  arabic: 0.98,
  homeroom: 1.02,
};

export const QUAL_FACTOR: Record<string, number> = {
  bach: 1,
  mast: 1.07,
  dip: 0.99,
  phd: 1.14,
};

function bdForTotal(
  total: number,
  opts: {
    weeklyHours?: number | null;
    familyAnnual?: number | null;
    eosMonthly?: number | null;
    specialAnnual?: number | null;
  } = {}
): CompensationBreakdown {
  const family = opts.familyAnnual ?? 0;
  const eosAnnual = (opts.eosMonthly ?? 0) * 12;
  const special = opts.specialAnnual ?? 0;
  const base = Math.round(total * 0.72);
  const extra = Math.round(total * 0.1);
  const transport = Math.round(total * 0.035);
  const nssf = Math.max(0, Math.round(total - base - extra - transport - family - eosAnnual - special));
  return {
    baseSalary: base,
    extraWages: extra,
    weeklyHours: opts.weeklyHours ?? 24,
    transport,
    nssf,
    familyAllowance: opts.familyAnnual != null && opts.familyAnnual > 0 ? opts.familyAnnual : null,
    endOfServiceMonthly: opts.eosMonthly != null && opts.eosMonthly > 0 ? opts.eosMonthly : null,
    specialTaskWages: opts.specialAnnual != null && opts.specialAnnual > 0 ? opts.specialAnnual : null,
  };
}

function person(
  id: string,
  name: string,
  emp: EmploymentType,
  roleId: string,
  qualId: string,
  total: number,
  opts?: Parameters<typeof bdForTotal>[1]
): TeacherEquityPerson {
  return {
    id,
    displayName: name,
    employmentType: emp,
    roleId,
    qualificationId: qualId,
    totalSalaryAnnual: total,
    breakdown: bdForTotal(total, opts),
  };
}

function shortName(full: string): string {
  return full.split('—')[0]?.trim() ?? full;
}

/** Five teachers per cohort — odd count for unambiguous median teacher. */
function cohortTeachers(
  schoolId: string,
  schoolName: string,
  emp: EmploymentType,
  totals: [number, number, number, number, number]
): TeacherEquityPerson[] {
  const sn = shortName(schoolName);
  const tag = emp === 'permanent' ? 'P' : 'C';
  const opts = [
    { weeklyHours: 20, familyAnnual: emp === 'permanent' ? 900 : undefined },
    { weeklyHours: 22, familyAnnual: emp === 'permanent' ? 1400 : undefined, eosMonthly: emp === 'permanent' ? 72 : undefined },
    { weeklyHours: 24, familyAnnual: emp === 'permanent' ? 2000 : undefined, eosMonthly: emp === 'permanent' ? 88 : undefined, specialAnnual: emp === 'permanent' ? 600 : undefined },
    { weeklyHours: 26, familyAnnual: emp === 'permanent' ? 2400 : undefined, eosMonthly: emp === 'permanent' ? 96 : undefined, specialAnnual: 400 },
    { weeklyHours: 28, familyAnnual: emp === 'permanent' ? 2600 : undefined, eosMonthly: emp === 'permanent' ? 105 : undefined, specialAnnual: emp === 'permanent' ? 2200 : 500 },
  ] as const;
  return totals.map((t, i) =>
    person(
      `${schoolId}-${tag}-${i + 1}`,
      `${sn} — ${['I', 'II', 'III', 'IV', 'V'][i]} (${tag})`,
      emp,
      'math',
      'bach',
      t,
      opts[i]
    )
  );
}

function schoolRecord(
  schoolId: string,
  schoolName: string,
  districtId: string,
  districtLabel: string,
  enId: string,
  enLab: string,
  tuId: string,
  tuLab: string,
  pbId: string,
  pbLab: string,
  permTotals: [number, number, number, number, number],
  contTotals: [number, number, number, number, number]
): SchoolEquityRecord {
  return {
    schoolId,
    schoolName,
    districtId,
    districtLabel,
    enrollmentBandId: enId,
    enrollmentBandLabel: enLab,
    tuitionBandId: tuId,
    tuitionBandLabel: tuLab,
    parentTuitionBandId: pbId,
    parentTuitionBandLabel: pbLab,
    permanentTeachers: cohortTeachers(schoolId, schoolName, 'permanent', permTotals),
    contractualTeachers: cohortTeachers(schoolId, schoolName, 'contractual', contTotals),
  };
}

export const MOCK_SCHOOLS: SchoolEquityRecord[] = [
  schoolRecord(
    's1',
    'Lycée Franco-Libanais — Verdun',
    'd3',
    'Beirut — District A',
    'en-md',
    'Medium (400–900)',
    'tu-high',
    'Tuition — Premium',
    'pb4',
    'Parent-facing tuition — Band 4',
    [17_800, 19_100, 22_400, 24_900, 28_200],
    [13_600, 14_800, 17_050, 18_900, 21_200]
  ),
  schoolRecord(
    's2',
    'École Pilote — Baabda',
    'd1',
    'Mount Lebanon — District 1',
    'en-lg',
    'Large (> 900)',
    'tu-mid',
    'Tuition — Mid',
    'pb3',
    'Parent-facing tuition — Band 3',
    [18_900, 20_400, 23_500, 26_200, 30_100],
    [14_800, 15_900, 18_400, 20_100, 22_600]
  ),
  schoolRecord(
    's3',
    'International College — Ain Aar',
    'd1',
    'Mount Lebanon — District 1',
    'en-lg',
    'Large (> 900)',
    'tu-high',
    'Tuition — Premium',
    'pb4',
    'Parent-facing tuition — Band 4',
    [20_200, 22_100, 25_600, 28_400, 32_500],
    [15_900, 17_200, 19_750, 21_800, 24_300]
  ),
  schoolRecord(
    's4',
    'Collège Notre-Dame — Tripoli',
    'd4',
    'North — District 1',
    'en-sm',
    'Small (< 400)',
    'tu-low',
    'Tuition — Standard',
    'pb1',
    'Parent-facing tuition — Band 1',
    [15_900, 17_200, 20_100, 22_400, 25_800],
    [12_400, 13_500, 15_720, 17_200, 19_400]
  ),
  schoolRecord(
    's5',
    'École Secondaire — Zahle',
    'd2',
    'Mount Lebanon — District 2',
    'en-md',
    'Medium (400–900)',
    'tu-mid',
    'Tuition — Mid',
    'pb2',
    'Parent-facing tuition — Band 2',
    [16_400, 18_000, 20_880, 23_100, 26_400],
    [13_100, 14_200, 16_500, 18_100, 20_050]
  ),
  schoolRecord(
    's6',
    'Collège Saint-Joseph — Jounieh',
    'd1',
    'Mount Lebanon — District 1',
    'en-sm',
    'Small (< 400)',
    'tu-mid',
    'Tuition — Mid',
    'pb2',
    'Parent-facing tuition — Band 2',
    [14_200, 16_800, 19_400, 24_600, 31_200],
    [11_500, 13_200, 15_100, 18_900, 23_400]
  ),
  schoolRecord(
    's7',
    'Lycée National — Saida',
    'd3',
    'Beirut — District A',
    'en-lg',
    'Large (> 900)',
    'tu-low',
    'Tuition — Standard',
    'pb1',
    'Parent-facing tuition — Band 1',
    [15_100, 16_200, 17_400, 18_600, 19_900],
    [12_000, 12_800, 13_700, 14_600, 15_500]
  ),
  schoolRecord(
    's8',
    'École Technique — Nabatieh',
    'd4',
    'North — District 1',
    'en-md',
    'Medium (400–900)',
    'tu-low',
    'Tuition — Standard',
    'pb1',
    'Parent-facing tuition — Band 1',
    [13_800, 18_500, 21_200, 26_800, 33_500],
    [10_900, 14_200, 16_500, 20_100, 25_800]
  ),
  schoolRecord(
    's9',
    'Académie Hariri — Beirut',
    'd3',
    'Beirut — District A',
    'en-lg',
    'Large (> 900)',
    'tu-high',
    'Tuition — Premium',
    'pb4',
    'Parent-facing tuition — Band 4',
    [19_500, 20_800, 22_100, 24_000, 27_200],
    [15_200, 16_100, 17_400, 18_800, 21_000]
  ),
  schoolRecord(
    's10',
    'Collège Maristes — Ashrafieh',
    'd3',
    'Beirut — District A',
    'en-md',
    'Medium (400–900)',
    'tu-mid',
    'Tuition — Mid',
    'pb3',
    'Parent-facing tuition — Band 3',
    [17_000, 19_200, 22_800, 27_500, 34_000],
    [13_400, 15_000, 17_800, 21_200, 26_500]
  ),
  schoolRecord(
    's11',
    'École Officielle — Hermel',
    'd4',
    'North — District 1',
    'en-sm',
    'Small (< 400)',
    'tu-low',
    'Tuition — Standard',
    'pb1',
    'Parent-facing tuition — Band 1',
    [14_500, 15_100, 15_800, 16_400, 17_200],
    [11_800, 12_200, 12_700, 13_100, 13_800]
  ),
  schoolRecord(
    's12',
    'Lycée des Filles — Baalbek',
    'd4',
    'North — District 1',
    'en-md',
    'Medium (400–900)',
    'tu-low',
    'Tuition — Standard',
    'pb2',
    'Parent-facing tuition — Band 2',
    [16_200, 17_500, 19_000, 21_400, 24_800],
    [12_900, 14_000, 15_200, 17_000, 19_600]
  ),
  schoolRecord(
    's13',
    'International School — Kesrouan',
    'd1',
    'Mount Lebanon — District 1',
    'en-lg',
    'Large (> 900)',
    'tu-high',
    'Tuition — Premium',
    'pb4',
    'Parent-facing tuition — Band 4',
    [21_000, 22_400, 24_100, 26_800, 29_500],
    [16_800, 17_900, 19_200, 21_100, 23_400]
  ),
  schoolRecord(
    's14',
    'Collège Patriarcal — Zgharta',
    'd4',
    'North — District 1',
    'en-sm',
    'Small (< 400)',
    'tu-mid',
    'Tuition — Mid',
    'pb2',
    'Parent-facing tuition — Band 2',
    [15_600, 16_900, 18_200, 19_800, 22_100],
    [12_500, 13_400, 14_500, 15_800, 17_600]
  ),
  schoolRecord(
    's15',
    'École Moderne — Tyre',
    'd2',
    'Mount Lebanon — District 2',
    'en-md',
    'Medium (400–900)',
    'tu-mid',
    'Tuition — Mid',
    'pb3',
    'Parent-facing tuition — Band 3',
    [18_400, 19_800, 21_500, 24_200, 28_900],
    [14_600, 15_700, 17_100, 19_200, 22_800]
  ),
];

export function scaleMoney(n: number, yearId: string, roleId: string, qualId: string): number {
  const f = (YEAR_FACTOR[yearId] ?? 1) * (ROLE_FACTOR[roleId] ?? 1) * (QUAL_FACTOR[qualId] ?? 1);
  return Math.round(n * f);
}

export function scalePerson(
  p: TeacherEquityPerson,
  yearId: string,
  roleId: string,
  qualId: string
): TeacherEquityPerson {
  const f = (YEAR_FACTOR[yearId] ?? 1) * (ROLE_FACTOR[roleId] ?? 1) * (QUAL_FACTOR[qualId] ?? 1);
  const r = (x: number) => Math.round(x * f);
  const b = p.breakdown;
  return {
    ...p,
    roleId,
    qualificationId: qualId,
    totalSalaryAnnual: r(p.totalSalaryAnnual),
    breakdown: {
      baseSalary: r(b.baseSalary),
      extraWages: r(b.extraWages),
      weeklyHours: b.weeklyHours,
      transport: r(b.transport),
      nssf: r(b.nssf),
      familyAllowance: b.familyAllowance != null ? r(b.familyAllowance) : null,
      endOfServiceMonthly: b.endOfServiceMonthly != null ? r(b.endOfServiceMonthly) : null,
      specialTaskWages: b.specialTaskWages != null ? r(b.specialTaskWages) : null,
    },
  };
}

export function teachersForEmployment(s: SchoolEquityRecord, emp: EmploymentType): TeacherEquityPerson[] {
  return emp === 'permanent' ? s.permanentTeachers : s.contractualTeachers;
}

/** Concrete (role, qualification) pairs for cohort math when filters use `FILTER_ALL`. */
export function roleQualPairs(roleId: string, qualId: string): { r: string; q: string }[] {
  const rids = roleId === FILTER_ALL ? ROLES.map((x) => x.id) : [roleId];
  const qids = qualId === FILTER_ALL ? QUALIFICATIONS.map((x) => x.id) : [qualId];
  const out: { r: string; q: string }[] = [];
  for (const r of rids) {
    for (const q of qids) {
      out.push({ r, q });
    }
  }
  return out;
}

export function scaledCohortTeachers(
  s: SchoolEquityRecord,
  emp: EmploymentType,
  yearId: string,
  roleId: string,
  qualId: string
): TeacherEquityPerson[] {
  const pairs = roleQualPairs(roleId, qualId);
  if (pairs.length === 1) {
    const pq = pairs[0]!;
    return teachersForEmployment(s, emp).map((p) => scalePerson(p, yearId, pq.r, pq.q));
  }
  const out: TeacherEquityPerson[] = [];
  for (const { r, q } of pairs) {
    for (const p of teachersForEmployment(s, emp)) {
      const sp = scalePerson(p, yearId, r, q);
      out.push({ ...sp, id: `${p.id}~${r}~${q}` });
    }
  }
  return out;
}

export interface CohortSnapshot {
  minPerson: TeacherEquityPerson;
  medianPerson: TeacherEquityPerson;
  maxPerson: TeacherEquityPerson;
  minTotal: number;
  medianTotal: number;
  maxTotal: number;
  spread: number;
}

export function getCohortSnapshot(
  s: SchoolEquityRecord,
  emp: EmploymentType,
  yearId: string,
  roleId: string,
  qualId: string
): CohortSnapshot {
  const list = [...scaledCohortTeachers(s, emp, yearId, roleId, qualId)].sort(
    (a, b) => a.totalSalaryAnnual - b.totalSalaryAnnual
  );
  const minPerson = list[0]!;
  const maxPerson = list[list.length - 1]!;
  const mid = Math.floor(list.length / 2);
  const medianPerson = list[mid]!;
  const minTotal = minPerson.totalSalaryAnnual;
  const maxTotal = maxPerson.totalSalaryAnnual;
  const medianTotal = medianPerson.totalSalaryAnnual;
  return {
    minPerson,
    medianPerson,
    maxPerson,
    minTotal,
    medianTotal,
    maxTotal,
    spread: Math.max(0, maxTotal - minTotal),
  };
}

export function schoolMedianForPeer(
  s: SchoolEquityRecord,
  emp: EmploymentType,
  yearId: string,
  roleId: string,
  qualId: string
): number {
  return getCohortSnapshot(s, emp, yearId, roleId, qualId).medianTotal;
}

function medianArr(values: number[]): number {
  const v = [...values].sort((a, b) => a - b);
  const m = Math.floor(v.length / 2);
  return v.length % 2 ? v[m]! : (v[m - 1]! + v[m]!) / 2;
}

export interface PeerRangeStats {
  minSchoolMedian: number;
  groupMedian: number;
  maxSchoolMedian: number;
  schoolMedians: { schoolId: string; schoolName: string; median: number }[];
  minMedianSchoolId: string;
  maxMedianSchoolId: string;
  nearestGroupMedianSchoolId: string;
}

function nearestSchoolIdToValue(
  rows: { schoolId: string; median: number }[],
  target: number
): string {
  let best = rows[0]!.schoolId;
  let bestD = Infinity;
  for (const r of rows) {
    const d = Math.abs(r.median - target);
    if (d < bestD) {
      bestD = d;
      best = r.schoolId;
    }
  }
  return best;
}

export function buildPeerRange(
  schools: SchoolEquityRecord[],
  groupKey: (s: SchoolEquityRecord) => string,
  groupId: string,
  emp: EmploymentType,
  yearId: string,
  roleId: string,
  qualId: string
): PeerRangeStats | null {
  const inGroup = schools.filter((s) => groupKey(s) === groupId);
  if (!inGroup.length) return null;
  const schoolMedians = inGroup.map((s) => ({
    schoolId: s.schoolId,
    schoolName: s.schoolName,
    median: schoolMedianForPeer(s, emp, yearId, roleId, qualId),
  }));
  const vals = schoolMedians.map((x) => x.median);
  const minSchoolMedian = Math.min(...vals);
  const maxSchoolMedian = Math.max(...vals);
  const groupMedian = medianArr(vals);
  const minMedianSchoolId = schoolMedians.find((r) => r.median === minSchoolMedian)!.schoolId;
  const maxMedianSchoolId = schoolMedians.find((r) => r.median === maxSchoolMedian)!.schoolId;
  const nearestGroupMedianSchoolId = nearestSchoolIdToValue(schoolMedians, groupMedian);
  return {
    minSchoolMedian,
    groupMedian,
    maxSchoolMedian,
    schoolMedians,
    minMedianSchoolId,
    maxMedianSchoolId,
    nearestGroupMedianSchoolId,
  };
}

export function positionOnRange(value: number, minV: number, maxV: number): number {
  const span = maxV - minV;
  if (span <= 0) return 50;
  return Math.min(100, Math.max(0, ((value - minV) / span) * 100));
}

export function variancePct(target: number, peer: number | null): number | null {
  if (peer == null || !Number.isFinite(peer) || peer === 0) return null;
  return ((target - peer) / peer) * 100;
}

export type PeerGroupKind = 'district' | 'enrollment' | 'tuition' | 'parent';

export function peerMedianForSchoolGroup(
  school: SchoolEquityRecord,
  kind: PeerGroupKind,
  emp: EmploymentType,
  yearId: string,
  roleId: string,
  qualId: string
): number | null {
  const keyFn =
    kind === 'district'
      ? (s: SchoolEquityRecord) => s.districtId
      : kind === 'enrollment'
        ? (s: SchoolEquityRecord) => s.enrollmentBandId
        : kind === 'tuition'
          ? (s: SchoolEquityRecord) => s.tuitionBandId
          : (s: SchoolEquityRecord) => s.parentTuitionBandId;
  const gid =
    kind === 'district'
      ? school.districtId
      : kind === 'enrollment'
        ? school.enrollmentBandId
        : kind === 'tuition'
          ? school.tuitionBandId
          : school.parentTuitionBandId;
  const pr = buildPeerRange(MOCK_SCHOOLS, keyFn, gid, emp, yearId, roleId, qualId);
  return pr?.groupMedian ?? null;
}

export interface OverviewRow {
  schoolId: string;
  schoolName: string;
  districtLabel: string;
  enrollmentBandLabel: string;
  tuitionBandLabel: string;
  parentTuitionBandLabel: string;
  internalSpread: number;
  cohortMedian: number;
  spreadHighlight: boolean;
  vsDistrictPct: number | null;
  vsEnrollmentPct: number | null;
  vsTuitionPct: number | null;
  vsParentPct: number | null;
}

export function buildOverviewRows(
  emp: EmploymentType,
  yearId: string,
  roleId: string,
  qualId: string,
  thresholds: TeacherEquitySpreadThresholds = defaultTeacherEquitySpreadThresholds()
): OverviewRow[] {
  return MOCK_SCHOOLS.map((s) => {
    const snap = getCohortSnapshot(s, emp, yearId, roleId, qualId);
    const cohortMedian = snap.medianTotal;
    const spreadHighlight =
      snap.spread > thresholds.highlightAmountUsd ||
      snap.spread > (thresholds.highlightPercentOfMedian / 100) * cohortMedian;
    const sm = schoolMedianForPeer(s, emp, yearId, roleId, qualId);
    return {
      schoolId: s.schoolId,
      schoolName: s.schoolName,
      districtLabel: s.districtLabel,
      enrollmentBandLabel: s.enrollmentBandLabel,
      tuitionBandLabel: s.tuitionBandLabel,
      parentTuitionBandLabel: s.parentTuitionBandLabel,
      internalSpread: snap.spread,
      cohortMedian,
      spreadHighlight,
      vsDistrictPct: variancePct(sm, peerMedianForSchoolGroup(s, 'district', emp, yearId, roleId, qualId)),
      vsEnrollmentPct: variancePct(sm, peerMedianForSchoolGroup(s, 'enrollment', emp, yearId, roleId, qualId)),
      vsTuitionPct: variancePct(sm, peerMedianForSchoolGroup(s, 'tuition', emp, yearId, roleId, qualId)),
      vsParentPct: variancePct(sm, peerMedianForSchoolGroup(s, 'parent', emp, yearId, roleId, qualId)),
    };
  });
}

/** System-wide median of school-level cohort medians (same role/qual/year slice). */
export function overallMedianSalary(
  emp: EmploymentType,
  yearId: string,
  roleId: string,
  qualId: string
): number {
  const medians = MOCK_SCHOOLS.map((s) => schoolMedianForPeer(s, emp, yearId, roleId, qualId));
  return medianArr(medians);
}

/** Mean internal salary spread across all schools for the slice. */
export function avgInternalSpreadSystemWide(
  emp: EmploymentType,
  yearId: string,
  roleId: string,
  qualId: string
): number {
  const rows = buildOverviewRows(emp, yearId, roleId, qualId);
  if (!rows.length) return 0;
  const sum = rows.reduce((a, r) => a + r.internalSpread, 0);
  return Math.round(sum / rows.length);
}

/** Average of the four peer variance columns (non-null only). */
export function avgPeerVariancePctForSchool(row: OverviewRow): number | null {
  const pts = [row.vsDistrictPct, row.vsEnrollmentPct, row.vsTuitionPct, row.vsParentPct].filter(
    (x): x is number => x != null && Number.isFinite(x)
  );
  if (!pts.length) return null;
  return Math.round((pts.reduce((a, b) => a + b, 0) / pts.length) * 10) / 10;
}

/** Mean of per-school average peer variance (system-wide equity pressure). */
export function avgPeerVarianceSystemWide(
  emp: EmploymentType,
  yearId: string,
  roleId: string,
  qualId: string
): number | null {
  const rows = buildOverviewRows(emp, yearId, roleId, qualId);
  const vals = rows.map(avgPeerVariancePctForSchool).filter((x): x is number => x != null && Number.isFinite(x));
  if (!vals.length) return null;
  return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
}

export type SpreadSeverityLevel = 'moderate' | 'high' | 'very_high';

export function spreadSeverityLevel(
  spread: number,
  meanSpread: number,
  thresholds: TeacherEquitySpreadThresholds = defaultTeacherEquitySpreadThresholds()
): SpreadSeverityLevel {
  if (meanSpread <= 0) {
    return spread > thresholds.severityVeryHighAmountUsd
      ? 'very_high'
      : spread > thresholds.severityHighAmountUsd
        ? 'high'
        : 'moderate';
  }
  const ratio = spread / meanSpread;
  if (ratio >= thresholds.severityVeryHighRatio) return 'very_high';
  if (ratio >= thresholds.severityHighRatio) return 'high';
  return 'moderate';
}

export function spreadSeverityLabel(s: SpreadSeverityLevel): string {
  if (s === 'very_high') return 'Very high';
  if (s === 'high') return 'High';
  return 'Moderate';
}

export const PEER_VARIANCE_DIMENSIONS_LABEL = 'District, school size, tuition, parent';

export interface ExecutiveTopSpreadRow {
  schoolId: string;
  schoolName: string;
  districtLabel: string;
  spread: number;
  severity: SpreadSeverityLevel;
  severityLabel: string;
}

export function topSchoolsByInternalSpread(
  emp: EmploymentType,
  yearId: string,
  roleId: string,
  qualId: string,
  limit = 15,
  thresholds: TeacherEquitySpreadThresholds = defaultTeacherEquitySpreadThresholds()
): ExecutiveTopSpreadRow[] {
  const rows = buildOverviewRows(emp, yearId, roleId, qualId, thresholds);
  const meanSpread = rows.length ? rows.reduce((a, r) => a + r.internalSpread, 0) / rows.length : 0;
  const sorted = [...rows].sort((a, b) => b.internalSpread - a.internalSpread);
  return sorted.slice(0, limit).map((r) => {
    const severity = spreadSeverityLevel(r.internalSpread, meanSpread, thresholds);
    return {
      schoolId: r.schoolId,
      schoolName: r.schoolName,
      districtLabel: r.districtLabel,
      spread: r.internalSpread,
      severity,
      severityLabel: spreadSeverityLabel(severity),
    };
  });
}

export type PeerComparisonTabMode = Exclude<ComparisonMode, 'intra_school'>;

export interface ExecutiveBelowPeerRow {
  schoolId: string;
  schoolName: string;
  districtLabel: string;
  avgPeerVariancePct: number | null;
  comparedAgainst: string;
  suggestedPeerTab: PeerComparisonTabMode;
}

export function worstPeerTabForSchool(
  schoolId: string,
  emp: EmploymentType,
  yearId: string,
  roleId: string,
  qualId: string
): PeerComparisonTabMode {
  const row = buildOverviewRows(emp, yearId, roleId, qualId).find((r) => r.schoolId === schoolId);
  if (!row) return 'by_district';
  const dims: { mode: PeerComparisonTabMode; pct: number | null }[] = [
    { mode: 'by_district', pct: row.vsDistrictPct },
    { mode: 'by_enrollment_band', pct: row.vsEnrollmentPct },
    { mode: 'by_tuition_band', pct: row.vsTuitionPct },
    { mode: 'by_parent_tuition', pct: row.vsParentPct },
  ];
  let best: PeerComparisonTabMode = 'by_district';
  let worstVal = 0;
  let first = true;
  for (const d of dims) {
    if (d.pct == null || !Number.isFinite(d.pct)) continue;
    if (first || d.pct < worstVal) {
      first = false;
      worstVal = d.pct;
      best = d.mode;
    }
  }
  return best;
}

/** Lowest average peer variance first (most negative = biggest concern). Only schools with negative average. */
export function topSchoolsBelowPeerMedian(
  emp: EmploymentType,
  yearId: string,
  roleId: string,
  qualId: string,
  limit = 15
): ExecutiveBelowPeerRow[] {
  const rows = buildOverviewRows(emp, yearId, roleId, qualId);
  const enriched = rows
    .map((r) => ({
      schoolId: r.schoolId,
      schoolName: r.schoolName,
      districtLabel: r.districtLabel,
      avgPeerVariancePct: avgPeerVariancePctForSchool(r),
      comparedAgainst: PEER_VARIANCE_DIMENSIONS_LABEL,
      suggestedPeerTab: worstPeerTabForSchool(r.schoolId, emp, yearId, roleId, qualId),
    }))
    .filter((x) => x.avgPeerVariancePct != null && x.avgPeerVariancePct < 0);
  enriched.sort((a, b) => {
    const na = a.avgPeerVariancePct!;
    const nb = b.avgPeerVariancePct!;
    return na - nb;
  });
  return enriched.slice(0, limit);
}

export function schoolById(id: string): SchoolEquityRecord | undefined {
  return MOCK_SCHOOLS.find((s) => s.schoolId === id);
}

/** Modal: median teacher for school at peer edge (min/max/nearest group). */
export function peerEdgeTeacher(
  schoolId: string,
  emp: EmploymentType,
  edge: 'minMedian' | 'group' | 'maxMedian',
  yearId: string,
  roleId: string,
  qualId: string
): TeacherEquityPerson {
  const s = schoolById(schoolId)!;
  const snap = getCohortSnapshot(s, emp, yearId, roleId, qualId);
  if (edge === 'minMedian') return snap.minPerson;
  if (edge === 'maxMedian') return snap.maxPerson;
  return snap.medianPerson;
}
